import React, { useState, useEffect, useRef } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { initializeCrop, createCroppedBlob } from "../pages/utility/image.jsx";
import ModalBasic from "./ModalBasic.jsx";
import FileUpload from "./FileUpload.jsx";
import { DangerButton, PrimaryButton, SecondaryButton } from "./Buttons.jsx";
import { addToast } from "../store/app/action.js";
import { useDispatch } from "react-redux";
import { uploadProductImageToS3 } from "../api/upload.js";
import { 
  createProductImage, 
  deleteProductImage 
} from "../api/productImages.js";
import { FaImage, FaTrash } from "react-icons/fa6";


const ProductImageModal = ({ skuData, setImageModalOpen, updateImageOverride }) => {
  const dispatch = useDispatch();
  const [image, setImage] = useState(null); // Single image override
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const processingRef = useRef(false);
  
  // Upload step state
  const [uploadStep, setUploadStep] = useState(1); // 1: select file, 2: crop, 3: uploading, 4: preview
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [fileKey, setFileKey] = useState(null);
  const [fileBucket, setFileBucket] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [crop, setCrop] = useState();
  const imageRef = useRef(null);

  // Load image override from product data when modal opens
  useEffect(() => {
    if (skuData) {
      // image_override comes from Typesense product data
      if (skuData.product?.image_override) {
        setImage({
          id: 'override', // Not needed but kept for compatibility
          sku_id: skuData.sku_id,
          image_url: skuData.product.image_override,
          created: new Date().toISOString(),
          updated: new Date().toISOString()
        });
      } else {
        setImage(null);
      }
      setLoading(false);
    }
  }, [skuData]);

  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    // Prevent multiple simultaneous processing
    if (processingRef.current) {
      return;
    }
    
    // Validate file types
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      dispatch(addToast({
        type: "error",
        message: `Invalid file types. Only JPEG, PNG, and WebP images are allowed.`,
        timer: 5
      }));
      return;
    }
    
    // Take only the first file
    const file = files[0];
    setSelectedFile(file);
    
    // Create preview URL for cropping
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
      // Initialize crop when image loads
      const img = new Image();
      img.src = reader.result;
      img.onload = () => {
        const initialCrop = initializeCrop(img.naturalWidth, img.naturalHeight, 1);
        setCrop(initialCrop);
      };
    };
    reader.readAsDataURL(file);
    
    // Move to crop step
    setUploadStep(2);
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile || !previewUrl || !crop || !imageRef.current) return;
    
    setUploading(true);
    setUploadStep(3);
    
    try {
      // Crop and create blob
      const blob = await createCroppedBlob(imageRef.current, crop, 1000, 1000, 0.9);
      
      // Upload cropped image to S3
      const uploadResult = await uploadProductImageToS3(blob);
      setFileUrl(uploadResult.url);
      setFileKey(uploadResult.key);
      setFileBucket(uploadResult.bucket);
      
      const imageData = {
        url: uploadResult.url,
        key: uploadResult.key,
        bucket: uploadResult.bucket
      };
      
      await createProductImage(skuData.sku_id, imageData);
      
      // Update local image state
      setImage({
        id: 'override',
        sku_id: skuData.sku_id,
        image_url: imageData.url,
        created: new Date().toISOString(),
        updated: new Date().toISOString()
      });
      
      // Update the hit in the list view immediately
      if (updateImageOverride) {
        updateImageOverride(skuData.sku_id, imageData.url);
      }
      
      dispatch(addToast({
        type: "success",
        message: image ? "Image override updated successfully" : "Image override uploaded successfully",
        timer: 4
      }));
      
      // Reset form
      setUploadStep(1);
      setSelectedFile(null);
      setFileUrl(null);
      setFileKey(null);
      setFileBucket(null);
      setPreviewUrl(null);
      setCrop(undefined);
    } catch (error) {
      console.error('Error creating image override:', error);
      dispatch(addToast({
        type: "error",
        message: "Error creating image override",
        timer: 5
      }));
      setUploadStep(2); // Go back to crop step on error
    } finally {
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    setUploadStep(1);
    setSelectedFile(null);
    setFileUrl(null);
    setFileKey(null);
    setFileBucket(null);
    setPreviewUrl(null);
    setCrop(undefined);
  };

  const handleImageDelete = async () => {
    if (!image) return;
    
    setDeleting(true);
    try {
      await deleteProductImage(skuData.sku_id);
      
      setImage(null);
      
      // Update the hit in the list view immediately (remove override)
      if (updateImageOverride) {
        updateImageOverride(skuData.sku_id, null);
      }
      
      dispatch(addToast({
        type: "success",
        message: "Image override removed successfully",
        timer: 3
      }));
    } catch (error) {
      console.error('Error deleting image override:', error);
      dispatch(addToast({
        type: "error",
        message: "Error removing image override",
        timer: 5
      }));
    } finally {
      setDeleting(false);
    }
  };

  if (!skuData) {
    return null;
  }

  const { sku_id, product } = skuData;
  const isVerifiedProduct = !sku_id.startsWith("CXP-");

  return (
    <ModalBasic
      modalOpen={Boolean(skuData)}
      setModalOpen={() => setImageModalOpen(null)}
      width="5xl"
      title={`Image Override for ${product.title} ${product.subtitle || ''}`}
    >
      <div className="space-y-6">
        {/* Product Info Header */}
        <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <div className="relative">
            <img 
              src={image?.image_url || product.image} 
              alt={product.title}
              className="w-16 h-16 rounded-lg object-cover flex-shrink-0" 
            />
            {image && (
              <div className="absolute -top-1 -right-1 bg-yellow-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" title="Image Override Active">
                !
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 truncate">
              {product.title} {product.subtitle || ''}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              SKU: {sku_id}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brand: {product.company}
            </p>
            {!isVerifiedProduct && (
              <p className="text-sm text-yellow-600 dark:text-yellow-400 mt-1">
                ⚠️ Image overrides are only available for verified products from the master menu
              </p>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <div className={`text-2xl font-bold ${image ? 'text-yellow-600 dark:text-yellow-400' : 'text-slate-600 dark:text-slate-400'}`}>
              {image ? '✓' : '—'}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-500">
              {image ? 'Override Active' : 'No Override'}
            </div>
          </div>
        </div>
        
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Image Override:</strong> Upload your own image to replace the verified product image. 
            If you upload a new image, it will replace the existing override. 
            You can remove the override at any time to revert to the original verified image.
          </p>
        </div>
        
        {/* Upload Section */}
        {isVerifiedProduct && (
          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-100 mb-3">
              {image ? 'Replace Image Override' : 'Upload Image Override'}
            </h4>
            
            {/* Step 1: File Selection */}
            {uploadStep === 1 && (
              <div className="space-y-4">
                <FileUpload
                  accept={["image/jpeg", "image/jpg", "image/png", "image/webp"]}
                  multiple={false}
                  onChange={handleFileUpload}
                  inputContent="Drag & drop an image here or click to browse (JPEG, PNG, WebP only)"
                />
              </div>
            )}
            
            {/* Step 2: Crop Image */}
            {uploadStep === 2 && previewUrl && crop && (
              <div className="space-y-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <h5 className="font-medium text-slate-800 dark:text-slate-100 mb-2">
                    Crop Your Image
                  </h5>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                    Adjust the crop area to select the portion of the image you want to use
                  </p>
                  <div className="relative w-full max-w-md mx-auto">
                    <ReactCrop
                      aspect={1}
                      crop={crop}
                      keepSelection
                      onChange={(c) => {
                        setCrop(c);
                      }}
                    >
                      <img 
                        ref={imageRef}
                        src={previewUrl} 
                        alt="Crop preview"
                        className="max-w-full"
                      />
                    </ReactCrop>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <SecondaryButton onClick={handleCancelUpload}>
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton 
                    onClick={handleConfirmUpload}
                    disabled={!crop || uploading}
                  >
                    Crop & Upload
                  </PrimaryButton>
                </div>
              </div>
            )}
            
            {/* Step 3: Uploading */}
            {uploadStep === 3 && (
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                <svg className="animate-spin w-8 h-8 mx-auto mb-4 text-slate-300 dark:text-slate-600" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                </svg>
                <p>Uploading image...</p>
              </div>
            )}
            
          </div>
        )}
        
        {/* Current Override */}
        <div>
          <h4 className="font-medium text-slate-800 dark:text-slate-100 mb-3">
            Current Override
          </h4>
          {loading ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <svg className="animate-spin w-8 h-8 mx-auto mb-4 text-slate-300 dark:text-slate-600" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
              </svg>
              <p>Loading image...</p>
            </div>
          ) : !image ? (
            <div className="text-center py-8 text-slate-500 dark:text-slate-400">
              <FaImage className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <p>No image override uploaded yet</p>
              {isVerifiedProduct && (
                <p className="text-sm">Upload an image using the area above</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Original Verified Image */}
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Original Verified Image
                </h5>
                <div className="w-full aspect-square bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                  <img 
                    src={product.image} 
                    alt="Original verified product"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
              
              {/* Override Image with Details */}
              <div className="bg-slate-50 dark:bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                    Your Override Image
                  </h5>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                    Active
                  </span>
                </div>
                <div className="w-full aspect-square bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden mb-3">
                  <img 
                    src={image.image_url} 
                    alt="Override product"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="space-y-2">
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Uploaded: {new Date(image.created).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 break-all">
                    {image.image_url}
                  </p>
                  <div className="pt-2">
                    <DangerButton
                      onClick={() => {
                        setShowDeleteModal(true);
                      }}
                      className="w-full"
                    >
                      <FaTrash className="w-4 h-4 mr-2 inline" />
                      Remove Override
                    </DangerButton>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Delete Confirmation Modal */}
        <ModalBasic
          modalOpen={showDeleteModal}
          setModalOpen={setShowDeleteModal}
          width="sm"
          title="Remove Image Override"
        >
          <div className="space-y-4">
            <p className="text-slate-600 dark:text-slate-400">
              Are you sure you want to remove this image override? The product will revert to using the verified image from the master menu.
            </p>
            <div className="flex gap-2 justify-end">
              <SecondaryButton onClick={() => setShowDeleteModal(false)}>
                Cancel
              </SecondaryButton>
              <DangerButton onClick={() => {
                handleImageDelete();
                setShowDeleteModal(false);
              }}>
                Remove Override
              </DangerButton>
            </div>
          </div>
        </ModalBasic>
      </div>
    </ModalBasic>
  );
};

export default ProductImageModal;

